/**
 * Iter 67eb — Booking-side trust-signal strip.
 *
 * Compressed version of the homepage "Why book with RentalCar.al?" grid,
 * tailored for the booking sidebar / mobile sheet. Sits directly above
 * the Reserve button so the renter sees the strongest conversion
 * objections answered just before they commit:
 *   1. No / low deposit, no credit card required
 *   2. Free cancellation up to 24h before pickup
 *   3. Transparent price — final total shown above
 *   4. Friendly local 24/7 support
 *
 * Visually compact (2x2 mini-grid) so it doesn't push the Reserve CTA
 * below the fold on shorter laptop viewports.
 */
import { ShieldCheck, CalendarClock, Tag, Headphones } from "lucide-react";

const SIGNALS = [
  {
    icon: ShieldCheck,
    title: "Low / no deposit",
    text: "No credit card required",
    fg: "text-emerald-600",
    bg: "bg-emerald-50",
    ring: "ring-emerald-100",
    testId: "booking-trust-deposit",
  },
  {
    icon: CalendarClock,
    title: "Free cancellation",
    text: "Up to 24h before pickup",
    fg: "text-violet-600",
    bg: "bg-violet-50",
    ring: "ring-violet-100",
    testId: "booking-trust-cancel",
  },
  {
    icon: Tag,
    title: "Transparent price",
    text: "Taxes & fees included",
    fg: "text-sky-600",
    bg: "bg-sky-50",
    ring: "ring-sky-100",
    testId: "booking-trust-price",
  },
  {
    icon: Headphones,
    title: "24/7 local support",
    text: "Help in EN / SQ / IT",
    fg: "text-rose-600",
    bg: "bg-rose-50",
    ring: "ring-rose-100",
    testId: "booking-trust-support",
  },
];

export default function BookingTrustSignals({ className = "" }) {
  return (
    <div
      className={`mt-5 grid grid-cols-2 gap-2 ${className}`}
      data-testid="booking-trust-signals"
    >
      {SIGNALS.map(({ icon: Icon, title, text, fg, bg, ring, testId }) => (
        <div
          key={title}
          className={`flex items-start gap-2 p-2.5 rounded-xl bg-white border border-neutral-200 ring-1 ${ring}`}
          data-testid={testId}
        >
          <div
            className={`h-7 w-7 shrink-0 rounded-lg ${bg} ring-1 ${ring} flex items-center justify-center`}
          >
            <Icon className={`h-3.5 w-3.5 ${fg}`} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold text-neutral-900 leading-tight truncate">
              {title}
            </div>
            <div className="text-[10px] text-neutral-500 leading-snug">
              {text}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
